import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, AppBar, Box, Button, Checkbox, Chip, Collapse, Container, FormControlLabel, IconButton, InputAdornment,
    Paper, Stack, Step, StepLabel, Stepper, TextField, Toolbar, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LogoutIcon from '@mui/icons-material/Logout';

/* Calculadora de importación Amazon → Guatemala vía casillero.
   Todo se guarda en este navegador (localStorage); no pasa por el backend. */
const STORAGE_KEY = 'importador.v1';
/* Divisor estándar de peso volumétrico en pulgadas (largo × ancho × alto / 166 = lb). */
const DIVISOR_VOLUMETRICO = 166;

const SUPUESTOS_BASE = {
    tc: '7.70',           // Q por US$
    tarifaLb: '4.50',     // US$ por libra del casillero
    salesTax: '7',        // % impuesto de venta en EE. UU. (dirección en Miami)
    dai: '10',            // % arancel (DAI), depende de la partida del producto
    iva: '12',            // % IVA Guatemala
    manejo: '0',          // Q fijos por paquete (desaduanaje, manejo)
    entrega: '25',        // Q entrega local al cliente
    comision: '0',        // % comisión de cobro (0 si es transferencia)
    margenMeta: '25',     // % margen mínimo para que valga la pena
};

const EJEMPLOS = [
    { nombre: 'Lámpara LED de escritorio (ejemplo)', link: '', precio: '22', envioUsa: '0', peso: '1.5', largo: '', ancho: '', alto: '', venta: '450', dai: '' },
    { nombre: 'Audífonos bluetooth (ejemplo)', link: '', precio: '25', envioUsa: '0', peso: '0.8', largo: '', ancho: '', alto: '', venta: '350', dai: '' },
    { nombre: 'Funda de celular (ejemplo)', link: '', precio: '9', envioUsa: '0', peso: '0.3', largo: '', ancho: '', alto: '', venta: '120', dai: '' },
    { nombre: 'Organizador de cocina (ejemplo)', link: '', precio: '18', envioUsa: '0', peso: '3', largo: '14', ancho: '10', alto: '8', venta: '250', dai: '' },
];

const PRODUCTO_VACIO = { nombre: '', link: '', precio: '', envioUsa: '0', peso: '', largo: '', ancho: '', alto: '', venta: '', dai: '' };

const n = (v) => { const x = parseFloat(String(v).replace(',', '.')); return Number.isFinite(x) ? x : 0; };
const fmtQ = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ', maximumFractionDigits: 2 });
let nextId = 1;
const conId = (p) => ({ ...p, id: nextId++ });

function cargar() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const data = JSON.parse(raw);
            return { supuestos: { ...SUPUESTOS_BASE, ...data.supuestos }, productos: (data.productos || []).map(conId) };
        }
    } catch { /* datos corruptos o storage bloqueado: arrancar con ejemplos */ }
    return { supuestos: SUPUESTOS_BASE, productos: EJEMPLOS.map(conId) };
}

/** Desglose de costo y ganancia por unidad. Montos en US$ hasta convertir; resultado final en Q. */
export function calcular(p, s) {
    const tc = n(s.tc);
    const precio = n(p.precio);
    const envioUsa = n(p.envioUsa);
    const pesoReal = n(p.peso);
    const pesoVol = n(p.largo) * n(p.ancho) * n(p.alto) / DIVISOR_VOLUMETRICO;
    const lbCobrables = Math.max(1, Math.ceil(Math.max(pesoReal, pesoVol)));

    const compraUsd = precio * (1 + n(s.salesTax) / 100) + envioUsa;
    const fleteUsd = lbCobrables * n(s.tarifaLb);
    const cifUsd = precio + envioUsa + fleteUsd;
    const daiPct = p.dai !== '' ? n(p.dai) : n(s.dai);
    const daiUsd = cifUsd * daiPct / 100;
    const ivaUsd = (cifUsd + daiUsd) * n(s.iva) / 100;

    const compra = compraUsd * tc;
    const flete = fleteUsd * tc;
    const impuestos = (daiUsd + ivaUsd) * tc;
    const extras = n(s.manejo) + n(s.entrega);
    const costo = compra + flete + impuestos + extras;

    const venta = n(p.venta);
    const comisionPct = n(s.comision) / 100;
    const comision = venta * comisionPct;
    const ganancia = venta - comision - costo;
    const margen = venta > 0 ? ganancia / venta : 0;
    const roi = costo > 0 ? ganancia / costo : 0;
    const meta = n(s.margenMeta) / 100;
    const divisor = 1 - meta - comisionPct;
    const precioMinimo = divisor > 0 ? costo / divisor : null;

    let veredicto = 'ok';
    if (!precio || !venta) veredicto = 'incompleto';
    else if (ganancia < 0) veredicto = 'perdida';
    else if (margen < meta) veredicto = 'bajo';

    return {
        pesoVol, lbCobrables, compra, flete, impuestos, daiPct, extras, costo,
        venta, comision, ganancia, margen, roi, precioMinimo, veredicto,
    };
}

const VEREDICTOS = {
    ok: { label: 'Vale la pena', color: 'success' },
    bajo: { label: 'Ganas poco', color: 'warning' },
    perdida: { label: 'Pierdes', color: 'error' },
    incompleto: { label: 'Faltan datos', color: 'default' },
};

function Veredicto({ v }) {
    const { label, color } = VEREDICTOS[v];
    return <Chip size="small" color={color} label={label} variant={v === 'incompleto' ? 'outlined' : 'filled'} />;
}

function Campo({ label, value, onChange, prefix, suffix, help, sx, big, autoFocus }) {
    return (
        <TextField
            size={big ? 'medium' : 'small'} label={label} value={value} helperText={help} autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            inputMode="decimal"
            slotProps={{
                input: {
                    startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined,
                    endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
                    sx: big ? { fontSize: 24, fontWeight: 700 } : undefined,
                },
            }}
            sx={{ minWidth: 0, ...sx }}
        />
    );
}

const PASOS = ['Producto', 'Precio', 'Peso', 'Venta', 'Resultado'];

function Pregunta({ titulo, ayuda, children }) {
    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{titulo}</Typography>
                {ayuda && <Typography color="text.secondary">{ayuda}</Typography>}
            </Box>
            {children}
        </Stack>
    );
}

function Asistente({ s, inicial, onGuardar, onCancelar }) {
    const [p, setP] = useState(inicial);
    const [paso, setPaso] = useState(0);
    const [grande, setGrande] = useState(Boolean(inicial.largo));
    const set = (k) => (v) => setP((x) => ({ ...x, [k]: v }));
    const r = calcular(p, s);
    const sugerido = r.precioMinimo != null ? Math.ceil(r.precioMinimo) : null;

    const listo = [
        p.nombre.trim() !== '',
        n(p.precio) > 0,
        n(p.peso) > 0,
        n(p.venta) > 0,
        true,
    ][paso];

    const siguiente = () => listo && setPaso((x) => Math.min(x + 1, PASOS.length - 1));
    const onKey = (e) => { if (e.key === 'Enter') siguiente(); };

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }} onKeyDown={onKey}>
            <Stepper activeStep={paso} alternativeLabel sx={{ mb: 4, '& .MuiStepLabel-label': { fontSize: 12 } }}>
                {PASOS.map((l) => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
            </Stepper>

            {paso === 0 && (
                <Pregunta titulo="¿Qué producto quieres traer?" ayuda="Solo un nombre para reconocerlo.">
                    <TextField autoFocus label="Nombre" placeholder="Ej. lámpara LED" value={p.nombre} onChange={(e) => set('nombre')(e.target.value)} />
                    <TextField size="small" label="Link de Amazon (opcional)" value={p.link} onChange={(e) => set('link')(e.target.value)} />
                </Pregunta>
            )}

            {paso === 1 && (
                <Pregunta titulo="¿Cuánto cuesta en Amazon?" ayuda="El precio en dólares que ves en la página.">
                    <Campo big autoFocus label="Precio" prefix="$" value={p.precio} onChange={set('precio')} />
                </Pregunta>
            )}

            {paso === 2 && (
                <Pregunta titulo="¿Cuánto pesa?" ayuda='Búscalo en Amazon en "Detalles del producto" → "Peso del artículo" o "Item weight".'>
                    <Campo big autoFocus label="Peso" suffix="lb" value={p.peso} onChange={set('peso')} />
                    <FormControlLabel
                        control={<Checkbox checked={grande} onChange={(e) => { setGrande(e.target.checked); if (!e.target.checked) setP((x) => ({ ...x, largo: '', ancho: '', alto: '' })); }} />}
                        label="Es una caja grande pero liviana (ej. almohada, organizador)"
                    />
                    {grande && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                El casillero cobra por tamaño si la caja es grande. Pon las medidas del paquete en pulgadas.
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
                                <Campo label="Largo" suffix="in" value={p.largo} onChange={set('largo')} />
                                <Campo label="Ancho" suffix="in" value={p.ancho} onChange={set('ancho')} />
                                <Campo label="Alto" suffix="in" value={p.alto} onChange={set('alto')} />
                            </Box>
                        </Box>
                    )}
                </Pregunta>
            )}

            {paso === 3 && (
                <Pregunta titulo="¿En cuánto lo venderías?" ayuda="En quetzales, lo que le cobrarías al cliente.">
                    <Campo big autoFocus label="Precio de venta" prefix="Q" value={p.venta} onChange={set('venta')} />
                    {sugerido != null && (
                        <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => set('venta')(String(sugerido))}>Usar Q{sugerido}</Button>}>
                            Traerlo te cuesta <b>{fmtQ.format(r.costo)}</b>. Para ganar bien cobra al menos <b>Q{sugerido}</b>.
                        </Alert>
                    )}
                </Pregunta>
            )}

            {paso === 4 && (
                <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
                    <Typography color="text.secondary">{p.nombre}</Typography>
                    <Box>
                        <Typography sx={{ fontSize: { xs: 40, sm: 52 }, fontWeight: 800, lineHeight: 1, color: r.ganancia < 0 ? 'error.main' : 'success.main' }}>
                            {r.ganancia < 0 ? 'Pierdes' : 'Ganas'} {fmtQ.format(Math.abs(r.ganancia))}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>por cada uno que vendas</Typography>
                    </Box>
                    <Veredicto v={r.veredicto} />
                    <Box sx={{ width: '100%', maxWidth: 360, textAlign: 'left' }}>
                        <Fila label="Lo vendes en" value={fmtQ.format(r.venta)} />
                        <Fila label="Traerlo te cuesta" value={`− ${fmtQ.format(r.costo + r.comision)}`} />
                        <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1, pt: 1 }}>
                            <Fila label="Te queda" value={fmtQ.format(r.ganancia)} strong />
                        </Box>
                    </Box>
                    {r.veredicto !== 'ok' && sugerido != null && (
                        <Typography variant="body2">
                            Para que valga la pena tendrías que venderlo en <b>Q{sugerido}</b> o más.
                            {r.pesoVol > n(p.peso) && ' Ojo: el casillero te cobra por el tamaño de la caja.'}
                        </Typography>
                    )}
                    <Detalle r={r} s={s} p={p} />
                </Stack>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4, flexWrap: 'wrap', gap: 1 }}>
                <Button onClick={paso === 0 ? onCancelar : () => setPaso(paso - 1)}>
                    {paso === 0 ? 'Cancelar' : 'Atrás'}
                </Button>
                {paso < 4 ? (
                    <Button variant="contained" disabled={!listo} onClick={siguiente}>Siguiente</Button>
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" color="error" onClick={() => onGuardar({ ...p, estado: 'descartado' })}>Descartar</Button>
                        <Button variant="contained" onClick={() => onGuardar({ ...p, estado: 'candidato' })}>Guardar</Button>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}

function Fila({ label, value, strong }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.4 }}>
            <Typography color={strong ? 'text.primary' : 'text.secondary'} sx={{ fontWeight: strong ? 700 : 400 }}>{label}</Typography>
            <Typography sx={{ fontWeight: strong ? 800 : 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        </Stack>
    );
}

/** Desglose plegado, para quien quiera ver de dónde sale el costo. */
function Detalle({ r, s, p }) {
    const [abierto, setAbierto] = useState(false);
    return (
        <Box sx={{ width: '100%', maxWidth: 360 }}>
            <Button size="small" onClick={() => setAbierto(!abierto)}>{abierto ? 'Ocultar detalle' : '¿De dónde sale el costo?'}</Button>
            <Collapse in={abierto}>
                <Box sx={{ textAlign: 'left', mt: 1 }}>
                    <Fila label={`Amazon (+${s.salesTax} % tax)`} value={fmtQ.format(r.compra)} />
                    <Fila label={`Casillero (${r.lbCobrables} lb${r.pesoVol > n(p.peso) ? ' por tamaño' : ''})`} value={fmtQ.format(r.flete)} />
                    <Fila label="Impuestos Guatemala" value={fmtQ.format(r.impuestos)} />
                    <Fila label="Entrega y manejo" value={fmtQ.format(r.extras)} />
                    {r.comision > 0 && <Fila label="Comisión de cobro" value={fmtQ.format(r.comision)} />}
                </Box>
            </Collapse>
        </Box>
    );
}

function Ajustes({ s, setSupuesto }) {
    const [abierto, setAbierto] = useState(false);
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography sx={{ fontWeight: 700 }}>Ajustes</Typography>
                    <Typography variant="body2" color="text.secondary">Se llenan una sola vez, con la cotización de tu casillero.</Typography>
                </Box>
                <Button size="small" onClick={() => setAbierto(!abierto)}>{abierto ? 'Cerrar' : 'Abrir'}</Button>
            </Stack>
            <Collapse in={abierto}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mt: 2 }}>
                    <Campo label="Tipo de cambio" prefix="Q" suffix="/ $" value={s.tc} onChange={setSupuesto('tc')} />
                    <Campo label="Tarifa casillero" prefix="$" suffix="/ lb" value={s.tarifaLb} onChange={setSupuesto('tarifaLb')} />
                    <Campo label="Tax en EE. UU." suffix="%" value={s.salesTax} onChange={setSupuesto('salesTax')} />
                    <Campo label="DAI (arancel)" suffix="%" value={s.dai} onChange={setSupuesto('dai')} />
                    <Campo label="IVA" suffix="%" value={s.iva} onChange={setSupuesto('iva')} />
                    <Campo label="Manejo por paquete" prefix="Q" value={s.manejo} onChange={setSupuesto('manejo')} />
                    <Campo label="Entrega local" prefix="Q" value={s.entrega} onChange={setSupuesto('entrega')} />
                    <Campo label="Comisión de cobro" suffix="%" value={s.comision} onChange={setSupuesto('comision')} />
                    <Campo label="Ganancia mínima" suffix="%" value={s.margenMeta} onChange={setSupuesto('margenMeta')} />
                </Box>
            </Collapse>
        </Paper>
    );
}

function ItemLista({ p, r, onAbrir, onEliminar }) {
    return (
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="button" onClick={onAbrir} sx={{
                all: 'unset', cursor: 'pointer', flex: 1, minWidth: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.nombre || 'Sin nombre'}
                    </Typography>
                    <Veredicto v={r.veredicto} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: r.ganancia < 0 ? 'error.main' : 'success.main' }}>
                    {r.ganancia < 0 ? '−' : '+'}{fmtQ.format(Math.abs(r.ganancia))}
                </Typography>
            </Box>
            <Tooltip title="Eliminar"><IconButton onClick={onEliminar} sx={{ mr: 1 }}><DeleteOutlineIcon /></IconButton></Tooltip>
        </Paper>
    );
}

export default function Importar() {
    const navigate = useNavigate();
    const [{ supuestos, productos }, setEstado] = useState(cargar);
    const [editando, setEditando] = useState(null); // null = lista; objeto = producto en el asistente

    useEffect(() => {
        try {
            const productosSinId = productos.map(({ id, ...rest }) => rest);
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ supuestos, productos: productosSinId }));
        } catch { /* storage bloqueado: la página sigue funcionando sin guardar */ }
    }, [supuestos, productos]);

    const setSupuesto = (k) => (v) => setEstado((e) => ({ ...e, supuestos: { ...e.supuestos, [k]: v } }));
    const eliminar = (id) => setEstado((e) => ({ ...e, productos: e.productos.filter((x) => x.id !== id) }));
    const guardar = (p) => {
        setEstado((e) => (p.id
            ? { ...e, productos: e.productos.map((x) => (x.id === p.id ? p : x)) }
            : { ...e, productos: [conId(p), ...e.productos] }));
        setEditando(null);
    };

    const [verDescartados, setVerDescartados] = useState(false);
    const lista = useMemo(
        () => productos.map((p) => ({ p, r: calcular(p, supuestos) })).sort((a, b) => b.r.ganancia - a.r.ganancia),
        [productos, supuestos]
    );
    const candidatos = lista.filter(({ p }) => p.estado !== 'descartado');
    const descartados = lista.filter(({ p }) => p.estado === 'descartado');

    const logout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        navigate('/login');
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0b1220' }}>
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>Importar</Typography>
                    <Button color="inherit" href="/panel">Solicitudes</Button>
                    <Button color="inherit" href="/panel/estudio">Estudio</Button>
                    <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>Salir</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" sx={{ py: 5 }}>
                {editando ? (
                    <Asistente key={editando.id || 'nuevo'} s={supuestos} inicial={editando} onGuardar={guardar} onCancelar={() => setEditando(null)} />
                ) : (
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800 }}>¿Gano o pierdo?</Typography>
                            <Typography color="text.secondary">Responde 4 preguntas y te digo si vale la pena traer un producto de Amazon.</Typography>
                        </Box>

                        <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setEditando({ ...PRODUCTO_VACIO })}>
                            Calcular un producto
                        </Button>

                        <Box>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Mis candidatos ({candidatos.length})</Typography>
                            {candidatos.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Aún no tienes productos guardados. Empieza con el botón de arriba.</Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {candidatos.map(({ p, r }) => (
                                        <ItemLista key={p.id} p={p} r={r} onAbrir={() => setEditando(p)} onEliminar={() => eliminar(p.id)} />
                                    ))}
                                </Stack>
                            )}
                        </Box>

                        {descartados.length > 0 && (
                            <Box>
                                <Button size="small" color="inherit" onClick={() => setVerDescartados(!verDescartados)}>
                                    {verDescartados ? 'Ocultar' : 'Ver'} descartados ({descartados.length})
                                </Button>
                                <Collapse in={verDescartados}>
                                    <Stack spacing={1} sx={{ mt: 1, opacity: 0.75 }}>
                                        {descartados.map(({ p, r }) => (
                                            <ItemLista key={p.id} p={p} r={r} onAbrir={() => setEditando(p)} onEliminar={() => eliminar(p.id)} />
                                        ))}
                                    </Stack>
                                </Collapse>
                            </Box>
                        )}

                        <Ajustes s={supuestos} setSupuesto={setSupuesto} />
                    </Stack>
                )}
            </Container>
        </Box>
    );
}
